/**
 * Hook para gerenciar a importação de listas de contatos via CSV
 * Gerencia validação, importação de contatos e criação da lista
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { normalizeBrazilianPhone, isSamePhone, createNormalizedPhoneSet } from '@/lib/phone-utils';
import { CSVRow, RowValidation, validatePhoneFormat, extractVariableColumns, generateListHash, normalizePhoneWithCountryCode } from '@/lib/csv-utils';
import { logger } from '@/lib/logger';

// =====================================================
// TIPOS
// =====================================================

/** Contato existente no sistema */
interface ExistingContact {
  id: string;
  phone: string;
  name: string | null;
  email: string | null;
}

/** Estatísticas de validação */
export interface ValidationStats {
  total: number;
  valid: number;
  invalid: number;
  duplicatesInCSV: number;
  existingInSystem: number;
  newContacts: number;
  /** Contatos na blacklist LGPD */
  blacklisted: number;
}

/** Progresso da importação */
export interface ImportProgress {
  current: number;
  total: number;
  phase: 'validating' | 'importing' | 'saving' | 'done';
  message: string;
}

/** Resultado da importação */
export interface ImportResult {
  success: boolean;
  listId?: string;
  hashCode?: string;
  imported: number;
  updated: number;
  skipped: number;
  errors: string[];
}

// =====================================================
// HOOK
// =====================================================

export function useListImport() {
  const { currentTenant, user } = useAuth();
  
  // Estados
  const [validatedRows, setValidatedRows] = useState<RowValidation[]>([]);
  const [validationStats, setValidationStats] = useState<ValidationStats | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  
  /**
   * Valida todas as linhas do CSV
   * Verifica formato de telefone, duplicatas e existência no sistema
   * @param rows - Linhas do CSV a validar
   * @param updateExisting - Se deve atualizar contatos existentes
   * @param allowDuplicates - Se permite linhas duplicadas (mesmo contato, dados diferentes)
   * @param defaultCountryCode - Código de país padrão para normalização (ex: '55' para Brasil)
   */
  const validateRows = useCallback(async (
    rows: CSVRow[],
    updateExisting: boolean = false,
    allowDuplicates: boolean = false,
    defaultCountryCode: string = '55'
  ): Promise<RowValidation[]> => {
    if (!currentTenant?.id) {
      throw new Error('Tenant não identificado');
    }
    
    setIsValidating(true);
    setProgress({
      current: 0,
      total: rows.length,
      phase: 'validating',
      message: 'Iniciando validação...'
    });
    
    try {
      // 1. Buscar contatos existentes do tenant
      setProgress(prev => prev ? { ...prev, message: 'Buscando contatos existentes...' } : null);
      
      const { data: existingContacts, error } = await supabase
        .from('contacts')
        .select('id, phone, name, email')
        .eq('tenant_id', currentTenant.id);
      
      if (error) throw error;
      
      // Criar Set para comparação rápida
      const existingPhoneSet = createNormalizedPhoneSet(
        existingContacts?.map(c => c.phone) || []
      );
      
      // Map para buscar dados do contato existente
      const existingContactMap = new Map<string, ExistingContact>();
      existingContacts?.forEach(contact => {
        if (contact.phone) {
          const normalized = normalizeBrazilianPhone(contact.phone);
          if (normalized) {
            existingContactMap.set(normalized, contact as ExistingContact);
          }
        }
      });
      
      // 2. Buscar blacklist do tenant para verificação LGPD
      setProgress(prev => prev ? { ...prev, message: 'Verificando blacklist LGPD...' } : null);
      
      const { data: blacklistData } = await supabase
        .from('contact_blacklist')
        .select('phone_normalized, name, reason')
        .eq('tenant_id', currentTenant.id);
      
      // Criar Map para busca rápida de blacklist
      const blacklistMap = new Map<string, { name: string | null; reason: string }>();
      blacklistData?.forEach(entry => {
        blacklistMap.set(entry.phone_normalized, {
          name: entry.name,
          reason: entry.reason
        });
      });
      
      // 2. Validar cada linha
      const validations: RowValidation[] = [];
      const seenPhones = new Set<string>();
      
      // Map para armazenar primeira ocorrência de cada telefone no CSV
      const phoneFirstOccurrence = new Map<string, { index: number; contactId?: string }>();
      
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const errors: string[] = [];
        
        // Atualizar progresso
        if (i % 100 === 0 || i === rows.length - 1) {
          setProgress({
            current: i + 1,
            total: rows.length,
            phase: 'validating',
            message: `Validando linha ${i + 1} de ${rows.length}...`
          });
        }
        
        // NORMALIZAÇÃO: Adicionar DDI automaticamente se ausente
        const phoneNormResult = normalizePhoneWithCountryCode(row.telefone, defaultCountryCode);
        const phoneToValidate = phoneNormResult.normalized;
        
        // Atualizar o row com telefone normalizado para uso posterior
        if (phoneNormResult.wasModified) {
          row.telefone = phoneToValidate;
        }
        
        // Validar formato do telefone (agora já normalizado com DDI)
        const phoneValidation = validatePhoneFormat(phoneToValidate);
        if (!phoneValidation.valid) {
          errors.push(phoneValidation.error || 'Telefone inválido');
        }
        
        // Normalizar telefone para comparação
        const phoneNormalized = normalizeBrazilianPhone(phoneToValidate);
        
        // Verificar duplicata no CSV
        const isDuplicate = phoneNormalized ? seenPhones.has(phoneNormalized) : false;
        
        // Obter primeira ocorrência (se for duplicata)
        const firstOccurrence = isDuplicate && phoneNormalized 
          ? phoneFirstOccurrence.get(phoneNormalized) 
          : undefined;
        
        if (isDuplicate) {
          // Se NÃO permite duplicatas, adiciona erro
          if (!allowDuplicates) {
            errors.push('Telefone duplicado no arquivo');
          }
          // Se permite duplicatas, não adiciona erro - apenas marca para UI
        } else if (phoneNormalized) {
          seenPhones.add(phoneNormalized);
        }
        
        // Verificar se existe no sistema
        const existsInSystem = phoneNormalized ? existingPhoneSet.has(phoneNormalized) : false;
        const existingContact = phoneNormalized ? existingContactMap.get(phoneNormalized) : undefined;
        
        // Verificar se está na blacklist LGPD
        const blacklistEntry = phoneNormalized ? blacklistMap.get(phoneNormalized) : undefined;
        const isBlacklisted = !!blacklistEntry;
        
      // IMPORTANTE: Blacklist NÃO adiciona erro ao array de erros
      // O campo isBlacklisted marca o contato para exibição na UI
      // O sistema excluirá automaticamente esses contatos durante a importação
      // Isso permite que o usuário avance mesmo com contatos na blacklist
        
        // Para duplicatas permitidas, vincular ao contactId da primeira ocorrência ou do sistema
        let effectiveContactId = existingContact?.id;
        
        if (!isDuplicate && phoneNormalized) {
          // Primeira ocorrência: registrar para uso futuro
          phoneFirstOccurrence.set(phoneNormalized, { 
            index: i, 
            contactId: existingContact?.id 
          });
        } else if (isDuplicate && allowDuplicates && firstOccurrence) {
          // Duplicata permitida: usar contactId da primeira ocorrência
          effectiveContactId = firstOccurrence.contactId;
        }
        
        validations.push({
          rowIndex: i,
          row,
          isValid: errors.length === 0,
          errors,
          phoneNormalized: phoneNormalized || undefined,
          isDuplicate,
          duplicateOfIndex: isDuplicate ? firstOccurrence?.index : undefined,
          existsInSystem: existsInSystem || (isDuplicate && allowDuplicates),
          contactId: effectiveContactId,
          isBlacklisted,
          blacklistReason: blacklistEntry?.reason
        });
      }
      
      // 3. Calcular estatísticas considerando allowDuplicates
      const duplicatesCount = validations.filter(v => v.isDuplicate).length;
      const invalidWithoutDuplicates = validations.filter(v => 
        !v.isValid && (!v.isDuplicate || !allowDuplicates)
      ).length;
      
      const stats: ValidationStats = {
        total: validations.length,
        valid: allowDuplicates
          ? validations.filter(v => v.isValid).length
          : validations.filter(v => v.isValid && !v.isDuplicate).length,
        invalid: allowDuplicates
          ? validations.filter(v => !v.isValid).length
          : validations.filter(v => !v.isValid || v.isDuplicate).length,
        duplicatesInCSV: duplicatesCount,
        existingInSystem: validations.filter(v => v.existsInSystem && !v.isDuplicate).length,
        newContacts: validations.filter(v => v.isValid && !v.isDuplicate && !v.existsInSystem && !v.isBlacklisted).length,
        blacklisted: validations.filter(v => v.isBlacklisted).length
      };
      
      setValidatedRows(validations);
      setValidationStats(stats);
      
      logger.info('[useListImport] Validação concluída:', JSON.stringify(stats));
      
      return validations;
      
    } finally {
      setIsValidating(false);
      setProgress(null);
    }
  }, [currentTenant?.id]);
  
  /**
   * Importa contatos e cria a lista
   * @param listName - Nome da lista
   * @param rows - Linhas do CSV
   * @param headers - Headers do CSV
   * @param validations - Validações das linhas
   * @param updateExisting - Se deve atualizar contatos existentes
   * @param allowDuplicates - Se permite linhas duplicadas (mesmo contato, dados diferentes)
   */
  const importList = useCallback(async (
    listName: string,
    rows: CSVRow[],
    headers: string[],
    validations: RowValidation[],
    updateExisting: boolean = false,
    allowDuplicates: boolean = false
  ): Promise<ImportResult> => {
    if (!currentTenant?.id || !user?.id) {
      return {
        success: false,
        imported: 0,
        updated: 0,
        skipped: 0,
        errors: ['Usuário ou tenant não identificado']
      };
    }
    
    setIsImporting(true);
    
    const errors: string[] = [];
    let imported = 0;
    let updated = 0;
    let skipped = 0;
    
    try {
      // Filtrar linhas válidas para importação
      // IMPORTANTE: Sempre excluir contatos na blacklist LGPD (proteção LGPD)
      // Se allowDuplicates, inclui duplicatas válidas; caso contrário, exclui duplicatas
      const blacklistedCount = validations.filter(v => v.isBlacklisted).length;
      const validRows = allowDuplicates
        ? validations.filter(v => v.isValid && !v.isBlacklisted)
        : validations.filter(v => v.isValid && !v.isDuplicate && !v.isBlacklisted);
      
      setProgress({
        current: 0,
        total: validRows.length,
        phase: 'importing',
        message: 'Importando contatos...'
      });
      
      // 1. Processar contatos em batch
      const contactIds: Map<number, string> = new Map();
      
      for (let i = 0; i < validRows.length; i++) {
        const validation = validRows[i];
        const row = validation.row;
        
        // Atualizar progresso
        if (i % 50 === 0 || i === validRows.length - 1) {
          setProgress({
            current: i + 1,
            total: validRows.length,
            phase: 'importing',
            message: `Processando contato ${i + 1} de ${validRows.length}...`
          });
        }
        
        try {
          // Caso 1: É uma duplicata permitida - apenas vincular ao contato existente
          if (validation.isDuplicate && allowDuplicates) {
            // Buscar contactId da primeira ocorrência ou do sistema
            const firstRowContactId = validation.duplicateOfIndex !== undefined 
              ? contactIds.get(validation.duplicateOfIndex) 
              : undefined;
            const effectiveContactId = firstRowContactId || validation.contactId;
            
            if (effectiveContactId) {
              contactIds.set(validation.rowIndex, effectiveContactId);
              skipped++; // Conta como skip na criação de contato (vinculou a existente)
            }
            continue;
          }
          
          // Caso 2: Contato existe no sistema
          if (validation.existsInSystem && validation.contactId) {
            if (updateExisting) {
              // Atualizar nome e email
              const updateData: { name?: string; email?: string } = {};
              if (row.nome) updateData.name = row.nome;
              if (row.email) updateData.email = row.email;
              
              if (Object.keys(updateData).length > 0) {
                await supabase
                  .from('contacts')
                  .update(updateData)
                  .eq('id', validation.contactId);
                updated++;
              } else {
                skipped++;
              }
            } else {
              skipped++;
            }
            contactIds.set(validation.rowIndex, validation.contactId);
          } else {
            // Caso 3: Criar novo contato OU recuperar existente (upsert seguro)
            // Usa UPSERT para evitar erro de duplicata em caso de:
            // - Cache de validação desatualizado
            // - Contato criado entre validação e importação
            // - Condição de corrida em processamento paralelo
            const { data: contactResult, error } = await supabase
              .from('contacts')
              .upsert(
                {
                  tenant_id: currentTenant.id,
                  phone: validation.phoneNormalized || row.telefone,
                  name: row.nome || null,
                  email: row.email || null
                },
                {
                  onConflict: 'tenant_id,phone',
                  ignoreDuplicates: false  // Retorna o registro (existente ou novo)
                }
              )
              .select('id')
              .single();
            
            if (error) throw error;
            
            // Como o upsert pode retornar registro existente ou novo, conta como importado/vinculado
            contactIds.set(validation.rowIndex, contactResult.id);
            imported++;
          }
        } catch (err) {
          // Erro específico de constraint (fallback caso upsert falhe por outro motivo)
          const pgError = err as { code?: string; message?: string };
          const isConstraintError = pgError.code === '23505';
          
          if (isConstraintError) {
            // Tentar recuperar o contato existente como fallback
            const { data: existingContact } = await supabase
              .from('contacts')
              .select('id')
              .eq('tenant_id', currentTenant.id)
              .eq('phone', validation.phoneNormalized || row.telefone)
              .maybeSingle();
            
            if (existingContact) {
              contactIds.set(validation.rowIndex, existingContact.id);
              skipped++; // Conta como skip pois já existia
              continue; // Prossegue sem adicionar erro
            }
          }
          
          const errorMsg = err instanceof Error ? err.message : 'Erro desconhecido';
          errors.push(`Linha ${validation.rowIndex + 2}: ${errorMsg}`);
          logger.error('[useListImport] Erro ao processar contato:', err);
        }
      }
      
      // 2. Criar a lista
      setProgress({
        current: 0,
        total: 1,
        phase: 'saving',
        message: 'Salvando lista...'
      });
      
      const hashCode = generateListHash();
      const variableColumns = extractVariableColumns(headers);
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30); // Expira em 30 dias (prazo aumentado para maior flexibilidade em campanhas)
      
      const { data: list, error: listError } = await supabase
        .from('campaign_lists')
        .insert({
          tenant_id: currentTenant.id,
          name: listName,
          hash_code: hashCode,
          total_rows: validRows.length,
          imported_contacts: imported,
          updated_contacts: updated,
          skipped_contacts: skipped + blacklistedCount, // Inclui contatos da blacklist como pulados
          columns: headers,
          variable_columns: variableColumns,
          expires_at: expiresAt.toISOString(),
          status: 'ready',
          created_by: user.id
        })
        .select('id')
        .single();
      
      if (listError) throw listError;
      
      // 3. Criar items da lista
      const listItems = validRows.map((validation, index) => {
        const row = validation.row;
        const variables: Record<string, string> = {};
        
        // Extrair variáveis customizadas
        variableColumns.forEach(col => {
          if (row[col]) {
            variables[col] = row[col];
          }
        });
        
        return {
          list_id: list.id,
          contact_id: contactIds.get(validation.rowIndex) || null,
          phone: validation.phoneNormalized || row.telefone,
          name: row.nome || null,
          email: row.email || null,
          variables,
          status: contactIds.has(validation.rowIndex) ? 'imported' : 'skipped',
          sort_order: index
        };
      });
      
      // Inserir em batches de 500
      const batchSize = 500;
      for (let i = 0; i < listItems.length; i += batchSize) {
        const batch = listItems.slice(i, i + batchSize);
        const { error: itemsError } = await supabase
          .from('campaign_list_items')
          .insert(batch);
        
        if (itemsError) {
          logger.error('[useListImport] Erro ao inserir items:', itemsError.message);
          errors.push(`Erro ao salvar items: ${itemsError.message}`);
        }
      }
      
      setProgress({
        current: 1,
        total: 1,
        phase: 'done',
        message: 'Lista criada com sucesso!'
      });
      
      logger.info('[useListImport] Lista criada:', `listId=${list.id}, hashCode=${hashCode}`);
      
      return {
        success: true,
        listId: list.id,
        hashCode,
        imported,
        updated,
        skipped,
        errors
      };
      
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro desconhecido';
      logger.error('[useListImport] Erro na importação:', errorMsg);
      
      return {
        success: false,
        imported,
        updated,
        skipped,
        errors: [...errors, errorMsg]
      };
      
    } finally {
      setIsImporting(false);
    }
  }, [currentTenant?.id, user?.id]);
  
  /**
   * Atualiza uma linha validada (para edição inline)
   */
  const updateValidatedRow = useCallback((rowIndex: number, field: string, value: string) => {
    setValidatedRows(prev => prev.map(v => {
      if (v.rowIndex === rowIndex) {
        const newRow = { ...v.row, [field]: value };
        
        // Revalidar se foi o telefone
        if (field === 'telefone') {
          const phoneValidation = validatePhoneFormat(value);
          const phoneNormalized = normalizeBrazilianPhone(value);
          
          return {
            ...v,
            row: newRow,
            isValid: phoneValidation.valid,
            errors: phoneValidation.valid ? [] : [phoneValidation.error || 'Telefone inválido'],
            phoneNormalized: phoneNormalized || undefined
          };
        }
        
        return { ...v, row: newRow };
      }
      return v;
    }));
  }, []);
  
  /**
   * Limpa o estado
   */
  const reset = useCallback(() => {
    setValidatedRows([]);
    setValidationStats(null);
    setProgress(null);
  }, []);
  
  return {
    // Estado
    validatedRows,
    validationStats,
    isValidating,
    isImporting,
    progress,
    
    // Ações
    validateRows,
    importList,
    updateValidatedRow,
    reset
  };
}
